import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError, CITIES, CUISINES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Zap, CheckCircle2, Truck } from "lucide-react";

const PERKS = [
  "Free forever on the Starter plan at launch",
  "Founding badge on your truck profile",
  "Priority placement when we go live",
  "Direct line to shape the product roadmap",
];

export default function Founding() {
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState(null);
  const [count, setCount] = useState(0);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    truck_name: "", owner_name: "", email: "", phone: "",
    city: "Green Bay", cuisine: "American", message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/founding-applications/count")
      .then((r) => { setRemaining(r.data.remaining); setCount(r.data.count); })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/founding-applications", form);
      setDone(data);
      toast.success("You're on the Founding 100 list!");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center">
      <CheckCircle2 className="w-16 h-16 text-[#0FB5A6] mx-auto" />
      <h1 className="font-display text-4xl font-black mt-4">You're #{done.position} on the list! 🎉</h1>
      <p className="text-slate-500 mt-3">Thanks for joining the FlavorFlow Founding 100. We've emailed your spot confirmation and will reach out as we launch across the Fox Valley.</p>
      <p className="text-sm text-slate-400 mt-2">{done.remaining} founding spots remaining.</p>
      <Button onClick={() => navigate("/")} className="mt-6 rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid="founding-home-btn">Back to home</Button>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-5 py-14 grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2635F] text-white text-xs font-bold uppercase tracking-widest">
          <Zap className="w-3.5 h-3.5" /> Founding 100 · Wisconsin launch
        </span>
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-5">Be one of the first 100 trucks.</h1>
        <p className="text-slate-500 mt-4 text-lg leading-relaxed">
          FlavorFlow launches first in Green Bay, Appleton, Oshkosh and the Fox Valley. Founding members help shape the future of food truck discovery in Wisconsin — and lock in perks for life.
        </p>

        {remaining !== null && (
          <div className="mt-8 bg-white rounded-2xl border border-teal-100 p-6 gr-shadow" data-testid="founding-counter">
            <div className="flex items-end justify-between">
              <div>
                <p className="font-display text-5xl font-black text-[#0FB5A6]">{remaining}</p>
                <p className="text-sm font-bold text-slate-500">founding spots left</p>
              </div>
              <p className="text-sm text-slate-400">{count}/100 claimed</p>
            </div>
            <div className="mt-4 h-2.5 rounded-full bg-teal-50 overflow-hidden">
              <div className="h-full bg-[#0FB5A6] rounded-full transition-all" style={{ width: `${Math.min(100, count)}%` }} />
            </div>
          </div>
        )}

        <ul className="mt-8 space-y-3">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-slate-700">
              <CheckCircle2 className="w-5 h-5 text-[#0FB5A6] shrink-0 mt-0.5" /> <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>

      <motion.form
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit} className="bg-white rounded-2xl border border-teal-100 p-7 gr-shadow space-y-4" data-testid="founding-form"
      >
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Truck className="w-5 h-5 text-[#0FB5A6]" /> Apply now</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Truck name"><Input required value={form.truck_name} onChange={(e) => set("truck_name", e.target.value)} data-testid="founding-truck" /></Field>
          <Field label="Your name"><Input required value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid="founding-name" /></Field>
          <Field label="Email"><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="founding-email" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="founding-phone" /></Field>
          <Field label="City">
            <Select value={form.city} onValueChange={(v) => set("city", v)}>
              <SelectTrigger data-testid="founding-city"><SelectValue /></SelectTrigger>
              <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Cuisine">
            <Select value={form.cuisine} onValueChange={(v) => set("cuisine", v)}>
              <SelectTrigger data-testid="founding-cuisine"><SelectValue /></SelectTrigger>
              <SelectContent>{CUISINES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Anything else? (optional)"><Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your truck…" data-testid="founding-message" /></Field>
        <Button type="submit" disabled={submitting} className="w-full rounded-full h-12 font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="founding-submit">
          {submitting ? "Submitting…" : "Claim my founding spot"}
        </Button>
      </motion.form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);
